import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Box,
  forwardRef,
  Grid,
  Stack,
  Text
} from '@chakra-ui/react'
import type {
  Offer,
  OfferLeasing,
  OfferVehicle,
  Part
} from 'BrokerTsLib/src/dtos'
import { Insurer, Product, Risk } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'

import { useApp } from '@/hooks/useApp'

import { usePartner } from '@/context/PartnerContext'
import { useTour } from '@reactour/tour'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { useGetRisks } from '../../hooks'
import { InsuranceType } from '../InsuranceQuoteItem/Summary/InsuranceType'
import { InsuranceValue } from '../InsuranceQuoteItem/Summary/InsuranceValue'
import { OfferPart } from '../InsuranceQuoteItem/Summary/OfferPart'

interface QuoteSummaryProps extends BoxProps {
  offer: OfferLeasing | Offer
}

interface PartWithChildren extends Part {
  children?: Part[]
}

export const PackageQuoteSummary = forwardRef(
  ({ offer, ...rest }: QuoteSummaryProps, ref) => {
    const { translate } = useTranslationsContext()
    const { product } = useApp()
    const { currency } = usePartner()
    const isAllianzVehicle = offer.insurerId == Insurer.Allianz && product == Product.VehicleInsurance
    const iov = offer as OfferVehicle

    // Transformace cen, aby se správně zobrazovalo zdarma nebo N/A.
    const parts = offer.parts.map(part => {
      let price = part.price

      // nová služba Allianz nevrací ceny jednotlivých rizik, ale pouze celkovou cenu
      // nahradíme ceny za undefined aby se nezobrazovaly jako "zdarma"
      if (isAllianzVehicle && (part?.price ?? 0) <= 0)
        price = undefined

      if ((price ?? 0) < 0)
        price = undefined

      return { ...part, price }
    })

    const povPart = parts.find((x) => x.riskId === Risk.POV)
    const havPart = parts.find((x) => x.riskId === Risk.HAV)
    const spolPart = parts.find((x) => x.riskId === Risk.SPOL)

    const mainRiskIds = [
      Risk.POV,
      Risk.HAV,
      Risk.SPOL
    ]

    if (isAllianzVehicle)
      mainRiskIds.push(Risk.SKLA)

    const editedParts = parts
      .filter(x => x.included)
      .map(p => {
        p.nameNew = p.packageItem?.selectedVariant?.insurerProductName ?? p.nameNew ?? p.name
        return p
      })

    const packageItems = editedParts.filter(x => x.packageItem?.included && !mainRiskIds.includes(x.riskId))
    const addons: PartWithChildren[] = [
      {
        riskId: Risk.BALICEK, name: `Balíček ${iov.packageName}`,
        children: packageItems,
        description: '',
        offerId: offer.offerId,
        price: packageItems.map(x => x.price).reduce((acc, val) => (acc ?? 0) + (val ?? 0), 0),
        riskType: 0,
        valid: true,
        included: true,
      },
      ...editedParts.filter(x => !x.packageItem?.included && !mainRiskIds.includes(x.riskId))
    ]

    const { data: risks } = useGetRisks()

    const povText = povPart?.nameNew ?? povPart?.name
    const havText = `${havPart?.nameNew ?? havPart?.name}, ${spolPart?.nameNew ?? spolPart?.name}`

    const { steps } = useTour()
    const isInTourContext = steps.length > 0

    return (
      <Grid ref={ref} {...rest}>

        {/* hlavni rizika - POV/HAV */}
        <Grid
          sx={{
            gridTemplateColumns: { md: '1fr 1fr' },
            alignContent: 'start',
            alignItems: 'start',
            columnGap: 8,
          }}
        >
          <Box minW={0}>
            {povPart && (
              <>
                <InsuranceType
                  label={translate('quoteItem.summary.povLabel', 'Povinné ručení')}
                  value={priceFormat(povPart.price, currency?.code)}
                  valid={povPart.valid}
                  errors={povPart.errors}
                />
                <InsuranceValue
                  value={povText!}
                  data-tour={isInTourContext && !havPart ? 'quote-item-product' : undefined}
                  tooltip={
                    <Stack gap={2}>
                      {povText && <Text fontWeight="bold">{povText}</Text>}
                      {povPart?.description && (
                        <Text
                          dangerouslySetInnerHTML={{
                            __html: povPart.description ?? '',
                          }}
                        ></Text>
                      )}
                    </Stack>
                  }
                />
              </>
            )}

            {!povPart && (
              <>
                <InsuranceType
                  label={translate('quoteItem.summary.povLabel', 'Povinné ručení')}
                  value={''}
                  valid={true}
                />
                <InsuranceValue value={<Text color={'gray.400'}>{translate('quoteItem.summary.noPov', 'Bez povinného ručení')}</Text>} />
              </>
            )}
          </Box>

          <Box minW={0}>
            {havPart && (
              <>
                <InsuranceType
                  label={translate('quoteItem.summary.havLabel', 'Havarijní pojištění')}
                  value={priceFormat(havPart.price, currency?.code)}
                  valid={havPart.valid}
                  errors={havPart.errors}
                />
                <InsuranceValue
                  value={havText!}
                  data-tour={isInTourContext ? 'quote-item-product' : undefined}
                  tooltip={
                    <Stack gap={2}>
                      {havText && <Text fontWeight="bold">{havText}</Text>}
                      {havPart?.description && (
                        <Text
                          dangerouslySetInnerHTML={{
                            __html: havPart.description ?? '',
                          }}
                        ></Text>
                      )}
                    </Stack>
                  }
                />
              </>
            )}

            {!havPart && (
              <>
                <InsuranceType
                  label={translate('quoteItem.summary.havLabel', 'Havarijní pojištění')}
                  value={''}
                  valid={true}
                />
                <InsuranceValue value={<Text color={'gray.400'}>{translate('quoteItem.summary.noHav', 'Bez havarijního pojištění')}</Text>} />
              </>
            )}
          </Box>
        </Grid>

        <Grid
          sx={{
            gridTemplateColumns: { md: '1fr' },
            alignContent: 'start',
            alignItems: 'start',
            columnGap: 8,
            pb: 4,
          }}
        >
          {addons
            .sort((x, y) => (x.name < y.name ? -1 : 1))
            .map((x) => (
              <OfferPart key={x.partId ?? x.riskId}
                {...x}
                label={x.riskId != Risk.BALICEK ? risks?.data.find((y) => y.id == x.riskId)?.name ?? '' : ''}
                showPrices={false}
                showChildrenPrices={true}
              />
            ))}
        </Grid>

      </Grid>
    )
  },
)
